"use client";

import { useCallback } from "react";

declare global {
	interface Window {
		Intercom: any;
		intercomSettings: any;
	}
}

const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID!;

/**
 * Transforms the API from `react-use-intercom` to match the expected API used by the Intercom JS library
 * 1. Converts keys from camelCase to snake_case.
 * 2. Converts `createdAt` / `created_at` date strings/objects to Unix timestamps (seconds).
 * @param props - The raw props object
 * @returns A new object formatted for the Intercom API
 */
const mapIntercomProps = (props?: Record<string, any>) => {
	if (!props) return {};

	return Object.keys(props).reduce((acc: Record<string, any>, key) => {
		const snakeKey = key.replace(
			/[A-Z]/g,
			(letter) => `_${letter.toLowerCase()}`,
		);
		let value = props[key];

		// The Intercom JS API (Not React) requires created_at to be a Unix timestamp in seconds.
		// If we detect an ISO string or Date object for 'created_at', convert it.
		if (
			snakeKey === "created_at" &&
			(typeof value === "string" || value instanceof Date)
		) {
			value = Math.floor(new Date(value).getTime() / 1000);
		}

		acc[snakeKey] = value;
		return acc;
	}, {});
};

/**
 * A custom hook to lazily load and interact with the Intercom Messenger.
 * The script is not injected into the DOM until `show()` is called.
 * @returns Object containing methods to interact with Intercom
 */
export const useLazyIntercom = () => {
	/**
	 * Initializes (boots) the Intercom instance with specific settings.
	 * If Intercom is not loaded, this function returns early (safe to call).
	 * @param props - Configuration options (User ID, settings, etc.)
	 */
	const boot = useCallback((props?: Record<string, any>) => {
		if (typeof window === "undefined" || !window.Intercom) {
			initialize();
		}

		window.Intercom("boot", {
			app_id: APP_ID,
			hide_default_launcher: true,
			...mapIntercomProps(props),
		});
	}, []);

	/**
	 * Internal Helper: Injects the real script and sets up the shim.
	 * This is only called when we are ready to BOOT (user clicked).
	 */
	const initialize = useCallback(() => {
		if (typeof window === "undefined" || window.Intercom) return;

		const intercom: any = (...args: any[]) => {
			intercom.c(args);
		};
		intercom.q = [];
		intercom.c = (args: any) => {
			intercom.q.push(args);
		};
		window.Intercom = intercom;

		// Early exit: If intercom-script tag is already available return
		if (document.getElementById("intercom-script")) return;

		const s = document.createElement("script");
		s.id = "intercom-script";
		s.type = "text/javascript";
		s.async = true;
		s.src = `https://widget.intercom.io/widget/${APP_ID}`;
		const x = document.getElementsByTagName("script")[0];
		x.parentNode?.insertBefore(s, x);
	}, []);

	/**
	 * Public: Prefetches the script files to the browser cache.
	 * Does NOT execute the script, preventing the "App ID not set" warning.
	 */
	const preload = useCallback(() => {
		if (
			typeof window === "undefined" ||
			document.getElementById("intercom-prefetch")
		)
			return;

		const link = document.createElement("link");
		link.id = "intercom-prefetch";
		link.rel = "preload";
		link.as = "script"; // Important! Tells browser this is a script
		link.href = `https://widget.intercom.io/widget/${APP_ID}`;
		document.head.appendChild(link);
	}, []);

	/**
	 * Loads the Intercom script (if needed) and opens the Messenger.
	 * 1. If Intercom is already loaded, it updates and opens the messenger.
	 * 2. If not loaded, it injects the script, boots the instance, and then opens.
	 * @param props - Optional props to boot with (e.g., pre-filling user data)
	 */
	const show = useCallback(
		(props?: Record<string, any>) => {
			initialize();
			boot(props);

			window.Intercom("show");
		},
		[initialize, boot],
	);

	/**
	 * Clears the Intercom session and resets the state.
	 * Should be called when the user logs out to prevent data leakage.
	 */
	const shutdown = useCallback(() => {
		if (!window.Intercom) return;

		window.Intercom("shutdown");
	}, []);

	/**
	 * Updates the currently running Intercom instance with new data.
	 * Useful for tracking page changes or updating user attributes without restarting.
	 * @param props - New attributes to update
	 */
	const update = useCallback((props: Record<string, any>) => {
		if (!window.Intercom) return;

		window.Intercom("update", mapIntercomProps(props));
	}, []);

	return { boot, preload, show, shutdown, update };
};
